import { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pill, SurfacePagePanel } from '@/design-system';
import { cn } from '@/shared/cn';
import type { CapacityRead } from '@/versions/v1/schema/capacity';
import { vizToneColor } from '@/versions/v1/modules/_viz';
import { ActorChip } from '../advisory/PipelineStrip';
import { BottleneckLadder } from './BottleneckLadder';
import { throughputProjection, type SelectableScenarioId } from './ThroughputTab.transform';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

/** The comp's 3px state rule across the top of a Step card, kept with the matte card's own hairline ring. */
const STEP_RULE = {
  ok: 'shadow-[inset_0_3px_0_var(--state-online),inset_0_0_0_1px_var(--card-inset-ring)]',
  bad: 'shadow-[inset_0_3px_0_var(--state-blocked),inset_0_0_0_1px_var(--card-inset-ring)]',
  neutral: 'shadow-[inset_0_3px_0_var(--ink-tertiary),inset_0_0_0_1px_var(--card-inset-ring)]',
  warn: 'shadow-[inset_0_3px_0_var(--state-warning),inset_0_0_0_1px_var(--card-inset-ring)]',
} as const;

const TONE_TEXT = {
  ok: 'text-[var(--state-online)]',
  bad: 'text-[var(--state-blocked)]',
  warn: 'text-[var(--state-warning)]',
  neutral: 'text-ink-primary',
} as const;

function useWidth<T extends HTMLElement>(initial: number) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(initial);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => setWidth(Math.round(element.getBoundingClientRect().width));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

export function ThroughputTab({ read, projectId }: { read: CapacityRead; projectId: string }) {
  const [selectedScenario, setSelectedScenario] = useState<SelectableScenarioId>('current');
  const [waterfallRef, waterfallWidth] = useWidth<HTMLDivElement>(360);
  const view = throughputProjection(read, selectedScenario, waterfallWidth);

  return (
    <div className="grid grid-cols-12 gap-3">
      <div id="walkthrough-capacity-ladder" className="col-span-12 flex min-w-0 flex-col xl:col-span-9">
        <BottleneckLadder
          read={read}
          strip={view.ladderStrip}
          meta={view.ladderMeta}
          feedLimited={view.feedLimited}
          selectedScenarioId={view.selectedScenarioId}
        />
      </div>

      <div className="col-span-12 flex min-w-0 flex-col gap-3 xl:col-span-3">
        {/* K18 r1: pl-4 pr-2 (not px-4) gives `US$52,932/day · today baseline`
            its pre-D26 single line at 1280 while the rows keep the title's
            left inset. */}
        <SurfacePagePanel title="AI analysis" actions={<ActorChip actor="Agent">AI</ActorChip>} contentClassName="pl-4 pr-2 py-1.5">
          <dl className="divide-y divide-[var(--color-divider)]">
            {view.ai.map((row) => (
              <div key={row.label} className="py-1.5">
                <dt className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">{row.label}</dt>
                <dd className="mt-0.5 text-sm font-semibold leading-snug tabular-nums text-ink-primary">{row.value}</dd>
              </div>
            ))}
          </dl>
        </SurfacePagePanel>

        <SurfacePagePanel title="Scenarios" contentClassName="px-2 py-2">
          <div className="flex flex-col">
            {view.scenarios.map((scenario) => {
              const pressed = selectedScenario === scenario.id;
              return (
                <button
                  key={scenario.id}
                  type="button"
                  aria-pressed={pressed}
                  disabled={!scenario.selectable}
                  title={[scenario.throughputComparison, scenario.recoveryComparison, scenario.ebitdaComparison].join(' · ')}
                  onClick={() => {
                    if (scenario.selectable) setSelectedScenario(scenario.id as SelectableScenarioId);
                  }}
                  // K18 r1: gap-1 gives the middle column back the width the
                  // wider US$ money column took, so `Current · 71.7 stph` and
                  // `Zn 82.3% · Pb 85.1%` stay on one line each.
                  className={cn(
                    'grid w-full grid-cols-[14px_minmax(0,1fr)_auto] items-center gap-1 rounded-[10px] border px-2.5 py-2 text-left transition-colors',
                    pressed
                      ? 'border-[color-mix(in_srgb,var(--color-accent-500)_40%,transparent)] bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))]'
                      : 'border-transparent',
                    scenario.selectable && !pressed && 'hover:bg-[var(--surface-1)]',
                    !scenario.selectable && 'cursor-not-allowed opacity-60',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'h-2.5 w-2.5 rounded-full border-2',
                      pressed ? 'border-[var(--color-accent-500)] bg-[var(--color-accent-500)]' : 'border-[var(--ink-tertiary)]',
                      !scenario.selectable && 'border-dashed',
                    )}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold leading-snug tabular-nums text-ink-primary">{scenario.name}</span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs tabular-nums text-muted">
                      {scenario.noCapital ? <Pill tone="danger" className="px-1.5 py-0 text-[10px] font-semibold tracking-[0.05em]">No capital</Pill> : null}
                      {scenario.sub}
                    </span>
                  </span>
                  <span className="text-right text-sm font-semibold tabular-nums text-ink-primary">
                    {scenario.ebitda}
                    <span className={cn('block text-xs font-medium', TONE_TEXT[scenario.valueSubTone])}>{scenario.valueSub}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </SurfacePagePanel>
      </div>

      <SurfacePagePanel
        title="Step 1"
        actions={<Pill tone={view.step1.tone === 'danger' ? 'danger' : 'success'} className="text-[10px] font-semibold tracking-[0.05em]">{view.step1.heading}</Pill>}
        className={cn('col-span-12 min-w-0 lg:col-span-4', STEP_RULE[view.step1.tone === 'danger' ? 'bad' : view.step1.tone === 'success' ? 'ok' : 'neutral'])}
        contentClassName="space-y-1.5"
      >
        <StepCardBody card={view.step1} />
      </SurfacePagePanel>

      <SurfacePagePanel
        title={view.step2.heading ?? 'Step 2'}
        actions={<Pill tone="warning" className="text-[10px] font-semibold tracking-[0.05em]">Capital opportunity</Pill>}
        className={cn('col-span-12 min-w-0 lg:col-span-4', STEP_RULE.warn)}
        contentClassName="space-y-1.5"
      >
        <StepCardBody card={view.step2} />
        <Link
          to={`/projects/${projectId}/cost?tab=improvements&opportunity=IO-10`}
          className="inline-flex pt-1 text-xs font-semibold text-[var(--color-accent-600)] hover:underline dark:text-[var(--color-accent-300)]"
        >
          Improvement options
        </Link>
      </SurfacePagePanel>

      <SurfacePagePanel
        title="EBITDA waterfall"
        meta={<span className="text-xs text-muted">{CURRENCY_GLYPH}/day</span>}
        className="col-span-12 min-w-0 lg:col-span-4"
      >
        <div ref={waterfallRef} className="min-w-0">
          <WaterfallChart projection={view.waterfall} />
        </div>
      </SurfacePagePanel>
    </div>
  );
}

function StepCardBody({ card }: { card: ReturnType<typeof throughputProjection>['step1'] }) {
  return (
    <>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 tabular-nums">
        <span className="text-sm font-semibold text-ink-primary">{card.fromTo}</span>
        <span className="text-xs text-muted">{card.additional}</span>
      </div>
      <dl className="divide-y divide-[var(--color-divider)]">
        {card.rows.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-3 py-[5px] text-xs">
            <dt className="text-ink-secondary">{row.label}</dt>
            <dd className="shrink-0 text-right font-semibold tabular-nums text-ink-primary">
              {row.tone && row.tone !== 'neutral' ? <span style={{ color: vizToneColor(row.tone) }}>{row.value}</span> : row.value}
            </dd>
          </div>
        ))}
      </dl>
    </>
  );
}

function WaterfallChart({ projection }: { projection: ReturnType<typeof throughputProjection>['waterfall'] }) {
  const drawn = projection.bars.filter((bar) => bar.drawn);
  const first = drawn[0]!;
  const last = drawn[drawn.length - 1]!;
  return (
    <svg
      viewBox={`0 0 ${projection.viewWidth} ${projection.viewHeight}`}
      role="img"
      aria-label={`EBITDA waterfall from ${first.label} ${first.valueLabel}/day to ${last.label} ${last.valueLabel}/day`}
      className="block h-auto w-full tabular-nums"
    >
      <line x1={0} x2={projection.viewWidth} y1={projection.baselineY} y2={projection.baselineY} stroke="var(--color-divider)" />
      {projection.connectors.map((connector, index) => (
        <line key={`connector-${index}`} x1={connector.x1} x2={connector.x2} y1={connector.y} y2={connector.y} stroke="var(--ink-tertiary)" strokeDasharray="3 3" />
      ))}
      {drawn.map((bar, index) => {
        const fill = bar.total ? (index === 0 ? 'var(--ink-secondary)' : 'var(--ink-tertiary)') : vizToneColor(bar.tone);
        const valueFill = bar.total ? 'var(--ink-primary)' : bar.tone === 'neutral' ? 'var(--ink-secondary)' : vizToneColor(bar.tone);
        return (
          <g key={bar.id}>
            <rect
              x={bar.x}
              y={bar.y}
              width={bar.width}
              height={bar.height}
              rx={3}
              fill={fill}
              fillOpacity={bar.total ? (index === 0 ? 0.9 : 0.55) : 0.9}
              stroke={bar.selected ? 'var(--color-accent-500)' : 'none'}
              strokeWidth={bar.selected ? 2.5 : 0}
            >
              <title>{bar.label} · {bar.valueLabel}/day</title>
            </rect>
            <text x={bar.x + bar.width / 2} y={bar.y - 5} textAnchor="middle" fontSize={projection.valueSize} fontWeight={600} fill={valueFill}>
              {bar.shortValue}
            </text>
            <text x={bar.x + bar.width / 2} y={projection.baselineY + 14} textAnchor="middle" fontSize={projection.labelSize} fontWeight={600} fill="var(--ink-secondary)">
              {bar.shortLabel}
            </text>
          </g>
        );
      })}
      {projection.selectedLanding ? (
        <g aria-label={`${projection.selectedLanding.scenarioLabel} EBITDA ${projection.selectedLanding.valueLabel}/day`}>
          <line
            x1={projection.selectedLanding.x - 7}
            x2={projection.selectedLanding.x + 7}
            y1={projection.selectedLanding.y}
            y2={projection.selectedLanding.y}
            stroke="var(--color-accent-500)"
            strokeWidth={2}
          />
          <circle cx={projection.selectedLanding.x} cy={projection.selectedLanding.y} r={3.5} fill="var(--color-accent-500)" stroke="var(--surface-2)" strokeWidth={1.5}>
            <title>{projection.selectedLanding.scenarioLabel} · {projection.selectedLanding.valueLabel}/day</title>
          </circle>
        </g>
      ) : null}
    </svg>
  );
}
